import { ForwardedRef, forwardRef, useState } from "react";
import { postApiMessages } from "../../../api/messages-client";

interface FloatingMessageBoxProps {
    userName: string;
    userId: string;
    profilePicture: string;
    listingId: string;
}

const FloatingMessageBox = forwardRef<HTMLDivElement, FloatingMessageBoxProps>(
    ({ userName, userId, profilePicture, listingId }, ref: ForwardedRef<HTMLDivElement>) => {
        const [message, setMessage] = useState("");
        const [sendingMessage, setSendingMessage] = useState(false);
        const [messageSent, setMessageSent] = useState(false);
        const [errorSending, setErrorSending] = useState(false);

        const handleSendMessage = async () => {
            setSendingMessage(true);
            setErrorSending(false);

            const postMessage = {
                receiver_id: userId,
                content: message,
                listing_id: listingId
            };

            try {
                const response = await postApiMessages({ requestBody: postMessage });

                if (response) {
                    setMessageSent(true);
                } else {
                    setErrorSending(true);
                }
            } catch (error) {
                console.error("Error sending message:", error);
                setErrorSending(true);
            } finally {
                setSendingMessage(false);
            }
        };

        return (
            <div ref={ref}>
                <div className="flex flex-col rounded-xl bg-white border border-gray-300 shadow max-w-lg">
                    <div className="p-2.5 max-w-lg">
                        <div className="flex-shrink-0 group block p-1.5 align-middle">
                            <div className="flex items-center">
                                <img 
                                    className="inline-block flex-shrink-0 size-[62px] rounded-full" 
                                    src={profilePicture} 
                                    alt="Profile picture" 
                                />
                                <div className="ms-3">
                                    <h3 className="font-medium text-gray-800">Owner - {userName}</h3>
                                    <p className="text-sm font-normal text-gray-400">Message now for free</p>
                                </div>
                            </div>
                        </div>

                        <div className="py-4">
                            <textarea
                                id="message"
                                rows={5}
                                className="py-3 px-4 block w-full border border-gray-300 resize-none rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                                placeholder="Write your message here..."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                disabled={sendingMessage || messageSent || errorSending}
                            />
                        </div>

                        <button 
                            type="button" 
                            className="w-full py-3 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent text-white bg-gradient-to-r from-[#24ccf2] to-[#2476f2] hover:opacity-90 disabled:opacity-50 disabled:pointer-events-none"
                            onClick={handleSendMessage}
                            disabled={sendingMessage || messageSent || errorSending}
                        >
                            {sendingMessage ? "Sending..." : "Send message"}
                        </button>

                        {messageSent && (
                            <p className="text-green-600 mt-2 pl-1 text-sm">Message sent successfully!</p>
                        )}

                        {errorSending && (
                            <p className="text-red-600 mt-2 pl-1 text-sm">Error sending message. Please try again.</p>
                        )}
                    </div>
                </div>
            </div>
        );
    }
);

export default FloatingMessageBox;